// Thao tác ghi của các màn cấu hình công lương. Mỗi hàm: kiểm quyền, kiểm luật (core/configRules),
// ghi AppSheet, ghi nhật ký. Lỗi nghiệp vụ ném Error với câu đọc được để màn hình hiện toast.
import { canDeclarePayrollConstant } from '../core/domain';
import {
    buildTemplateColumnRows, effectiveFormulaToday, findMissingSeedRows, findVariableUsages,
    normalizeVariableInput, validateAdvanceInput, validateDateRange, validateFormulaConfiguration,
} from '../core/configRules';
import { newId } from './appsheet';
import { assertCanManagePayroll, currentPayrollUser } from './permissions';
import { PAYROLL_TABLES, fromSheetRow, payrollRepos } from './tables';

export async function writeAudit(action, detail, maKy = '') {
    try {
        await payrollRepos.auditLog.add([{
            ID: newId('NK'),
            THOI_GIAN: new Date(),
            NGUOI: currentPayrollUser().username,
            HANH_DONG: action,
            MA_KY: maKy,
            CHI_TIET: typeof detail === 'string' ? detail : JSON.stringify(detail),
        }]);
    } catch (error) {
        // Nhật ký hỏng không được chặn thao tác chính đã ghi xong, nhưng phải để lại dấu vết.
        console.error('Không ghi được nhật ký công lương', error);
    }
}

// ===== DANH MỤC BIẾN, HẰNG SỐ, CÔNG THỨC =====

export async function loadConfiguration() {
    const [variables, constants, formulas, templates, templateColumns, agreements] = await Promise.all([
        payrollRepos.variables.list(),
        payrollRepos.constants.list(),
        payrollRepos.formulas.list(),
        payrollRepos.templates.list(),
        payrollRepos.templateColumns.list(),
        payrollRepos.agreements.list(),
    ]);
    return {
        variables: variables.sort((left, right) => (left.THU_TU ?? 0) - (right.THU_TU ?? 0) || left.MA_BIEN.localeCompare(right.MA_BIEN)),
        constants, formulas, templates, templateColumns, agreements,
    };
}

/** Thêm phần còn thiếu của danh mục mặc định; không đụng tới những gì đã có. */
export async function seedMissingCatalog(config) {
    assertCanManagePayroll();
    const missing = findMissingSeedRows(config);
    const toObjects = (schema, rows) => rows.map((row) => fromSheetRow(schema, row));
    await payrollRepos.variables.add(toObjects(PAYROLL_TABLES.variables, missing.variables));
    await payrollRepos.constants.add(toObjects(PAYROLL_TABLES.constants, missing.constants));
    await payrollRepos.formulas.add(toObjects(PAYROLL_TABLES.formulas, missing.formulas));
    await payrollRepos.templates.add(toObjects(PAYROLL_TABLES.templates, missing.templates));
    await payrollRepos.templateColumns.add(toObjects(PAYROLL_TABLES.templateColumns, missing.templateColumns));
    const counts = Object.fromEntries(Object.entries(missing).map(([key, rows]) => [key, rows.length]));
    await writeAudit('Khởi tạo danh mục lương', counts);
    return counts;
}

/**
 * Lưu biến. Biến công thức: công thức mới được thêm thành một đời có ngày hiệu lực (kỳ cũ vẫn tính
 * bằng công thức cũ), và công thức hiện hành trên danh mục là đời đang áp dụng hôm nay.
 */
export async function saveVariable(input, { isNew, config, formulaFrom }) {
    assertCanManagePayroll();
    const { variable, errors } = normalizeVariableInput(input, { existingCodes: config.variables.map((item) => item.MA_BIEN), isNew });
    if (errors.length) throw new Error(errors.join('\n'));
    const formulaError = validateFormulaConfiguration(config.variables, { code: variable.MA_BIEN, formula: variable.LOAI_BIEN === 'CONG_THUC' ? variable.CONG_THUC : null });
    if (formulaError) throw new Error(formulaError);

    const previous = config.variables.find((item) => item.MA_BIEN === variable.MA_BIEN);
    const versions = config.formulas.filter((row) => row.MA_BIEN === variable.MA_BIEN);
    const formulaChanged = variable.LOAI_BIEN === 'CONG_THUC' && (!previous || previous.CONG_THUC !== variable.CONG_THUC || !versions.length);
    if (formulaChanged) {
        const rangeError = validateDateRange(formulaFrom, null, 'Công thức có hiệu lực');
        if (rangeError) throw new Error(rangeError);
        const version = { ID: newId('CT'), MA_BIEN: variable.MA_BIEN, HIEU_LUC_TU: formulaFrom, HIEU_LUC_DEN: null, CONG_THUC: variable.CONG_THUC, GHI_CHU: '', NGUOI_TAO: currentPayrollUser().username, UPDATED_AT: new Date() };
        await payrollRepos.formulas.add([version]);
        // Công thức hiện hành là đời đang áp dụng hôm nay: khai đời có hiệu lực từ tháng sau thì hôm nay vẫn dùng đời cũ.
        variable.CONG_THUC = effectiveFormulaToday([...versions, version])?.CONG_THUC ?? (previous?.CONG_THUC || variable.CONG_THUC);
    }
    if (isNew) await payrollRepos.variables.add([variable]);
    else await payrollRepos.variables.update([variable]);
    await writeAudit(isNew ? 'Thêm biến lương' : 'Sửa biến lương', variable);
    return variable;
}

export async function setVariableActive(code, active) {
    assertCanManagePayroll();
    await payrollRepos.variables.update([{ MA_BIEN: code, HOAT_DONG: active }]);
    await writeAudit(active ? 'Dùng lại biến lương' : 'Ngừng dùng biến lương', code);
}

/** Xóa biến; biến còn được dùng thì chuyển sang ngừng dùng thay vì xóa. Trả về thông báo kết quả. */
export async function deleteVariable(code, config) {
    assertCanManagePayroll();
    const reasons = findVariableUsages(code, config);
    if (reasons.length) {
        const variable = config.variables.find((item) => item.MA_BIEN === code);
        if (!variable?.HOAT_DONG) throw new Error(`Biến ${code} đang ngừng dùng và vẫn còn được dùng ở ${reasons.join('; ')} nên không xóa hẳn được.`);
        await setVariableActive(code, false);
        return `Biến ${code} đang được dùng ở ${reasons.join('; ')} nên đã chuyển sang ngừng dùng thay vì xóa.`;
    }
    const constantIds = config.constants.filter((row) => row.MA_BIEN === code).map((row) => row.ID);
    const formulaIds = config.formulas.filter((row) => row.MA_BIEN === code).map((row) => row.ID);
    if (constantIds.length) await payrollRepos.constants.remove(constantIds);
    if (formulaIds.length) await payrollRepos.formulas.remove(formulaIds);
    await payrollRepos.variables.remove([code]);
    await writeAudit('Xóa biến lương', code);
    return `Đã xóa biến ${code}.`;
}

/** Thêm một mức hằng số mới. Mức cũ giữ nguyên làm lịch sử; khai chồng ngày thì mức cập nhật sau thắng. */
export async function addConstant(variable, { GIA_TRI, HIEU_LUC_TU, HIEU_LUC_DEN, GHI_CHU }) {
    assertCanManagePayroll();
    if (!canDeclarePayrollConstant(variable.LOAI_BIEN, variable.NGUON_DU_LIEU, variable.MA_BIEN)) {
        throw new Error('Biến này không khai được hằng số công ty (chỉ biến Hệ thống nguồn Hằng số công ty, hoặc biến Nhập tay trừ NC/GTC).');
    }
    if (GIA_TRI === null || GIA_TRI === undefined || Number.isNaN(GIA_TRI)) throw new Error('Giá trị phải là số.');
    const rangeError = validateDateRange(HIEU_LUC_TU, HIEU_LUC_DEN);
    if (rangeError) throw new Error(rangeError);
    const row = { ID: newId('HS'), MA_BIEN: variable.MA_BIEN, HIEU_LUC_TU, HIEU_LUC_DEN: HIEU_LUC_DEN || null, GIA_TRI, GHI_CHU: GHI_CHU || '', NGUOI_TAO: currentPayrollUser().username, UPDATED_AT: new Date() };
    await payrollRepos.constants.add([row]);
    await writeAudit('Thêm mức hằng số', `${variable.MA_BIEN}: ${GIA_TRI} từ ${HIEU_LUC_TU.toLocaleDateString('vi-VN')}`);
    return row;
}

export async function deleteConstant(row) {
    assertCanManagePayroll();
    await payrollRepos.constants.remove([row.ID]);
    await writeAudit('Xóa mức hằng số', `${row.MA_BIEN}: ${row.GIA_TRI}`);
}

/** Thêm một đời công thức; công thức hiện hành trên danh mục cập nhật theo đời áp dụng hôm nay. */
export async function addFormulaVersion(variable, { CONG_THUC, HIEU_LUC_TU, HIEU_LUC_DEN, GHI_CHU }, config) {
    assertCanManagePayroll();
    if (variable.LOAI_BIEN !== 'CONG_THUC') throw new Error('Chỉ biến công thức mới khai được công thức theo ngày hiệu lực.');
    const formula = String(CONG_THUC ?? '').trim().toUpperCase();
    if (!formula) throw new Error('Công thức không được để trống.');
    const rangeError = validateDateRange(HIEU_LUC_TU, HIEU_LUC_DEN);
    if (rangeError) throw new Error(rangeError);
    const formulaError = validateFormulaConfiguration(config.variables, { code: variable.MA_BIEN, formula });
    if (formulaError) throw new Error(formulaError);
    const row = { ID: newId('CT'), MA_BIEN: variable.MA_BIEN, HIEU_LUC_TU, HIEU_LUC_DEN: HIEU_LUC_DEN || null, CONG_THUC: formula, GHI_CHU: GHI_CHU || '', NGUOI_TAO: currentPayrollUser().username, UPDATED_AT: new Date() };
    await payrollRepos.formulas.add([row]);
    await syncCurrentFormula(variable, [...config.formulas.filter((item) => item.MA_BIEN === variable.MA_BIEN), row]);
    await writeAudit('Thêm đời công thức', `${variable.MA_BIEN}: ${formula}`);
    return row;
}

export async function deleteFormulaVersion(variable, row, config) {
    assertCanManagePayroll();
    await payrollRepos.formulas.remove([row.ID]);
    await syncCurrentFormula(variable, config.formulas.filter((item) => item.MA_BIEN === variable.MA_BIEN && item.ID !== row.ID));
    await writeAudit('Xóa đời công thức', `${variable.MA_BIEN}: ${row.CONG_THUC}`);
}

// Không còn đời nào phủ hôm nay thì giữ công thức cũ trên danh mục để bảng lương không rỗng công thức.
async function syncCurrentFormula(variable, versions) {
    const active = effectiveFormulaToday(versions);
    if (active && active.CONG_THUC !== variable.CONG_THUC) {
        await payrollRepos.variables.update([{ MA_BIEN: variable.MA_BIEN, CONG_THUC: active.CONG_THUC }]);
    }
}

// ===== MẪU BẢNG LƯƠNG =====

export async function loadTemplateData() {
    const [config, employees, allocations] = await Promise.all([
        loadConfiguration(),
        payrollRepos.employees.list(),
        payrollRepos.allocations.list(),
    ]);
    return { ...config, employees, allocations };
}

/**
 * Lưu mẫu và cột của mẫu. Ghi cột trước, đầu mẫu sau: lỗi giữa chừng thì lưu lại là đủ vì khóa cột
 * cố định (MÃ MẪU|MÃ BIẾN). Cột bị bỏ khỏi mẫu thì xóa.
 */
export async function saveTemplate(template, columns, { isNew, previousColumns = [] }) {
    assertCanManagePayroll();
    if (!isNew && template.KHOA_THAY_DOI) throw new Error('Mẫu đã dùng trong kỳ lương đã duyệt nên bị khóa. Hãy sao chép thành mẫu mới.');
    const rows = buildTemplateColumnRows(template.MA_MAU, columns);
    const keepIds = new Set(rows.map((row) => row.ID));
    const removedIds = previousColumns.filter((column) => !keepIds.has(column.ID)).map((column) => column.ID);
    const existingIds = new Set(previousColumns.map((column) => column.ID));
    const added = rows.filter((row) => !existingIds.has(row.ID));
    const updated = rows.filter((row) => existingIds.has(row.ID));
    if (added.length) await payrollRepos.templateColumns.add(added);
    if (updated.length) await payrollRepos.templateColumns.update(updated);
    if (removedIds.length) await payrollRepos.templateColumns.remove(removedIds);
    const header = {
        MA_MAU: template.MA_MAU,
        TEN_MAU: String(template.TEN_MAU).trim(),
        MO_TA: template.MO_TA || '',
        HOAT_DONG: template.HOAT_DONG !== false,
        TO_LIST: template.TO_LIST || [],
        NHAN_SU_LIST: template.NHAN_SU_LIST || [],
        NHAN_SU_LOAI_TRU: template.NHAN_SU_LOAI_TRU || [],
        PHIEN_BAN: isNew ? 1 : (Number(template.PHIEN_BAN) || 1) + 1,
        KHOA_THAY_DOI: false,
        ...(isNew ? { NGUOI_TAO: currentPayrollUser().username } : {}),
    };
    if (isNew) await payrollRepos.templates.add([header]);
    else await payrollRepos.templates.update([header]);
    await writeAudit(isNew ? 'Thêm mẫu bảng lương' : 'Sửa mẫu bảng lương', { ...header, cot: rows.map((row) => row.BIEN_ID) });
    return header;
}

/** Xóa mẫu; mẫu đã có thỏa thuận hoặc kỳ lương dùng thì chỉ ngưng hoạt động. */
export async function deleteTemplate(template, { agreements, templateColumns }) {
    assertCanManagePayroll();
    const usedByAgreements = agreements.filter((agreement) => agreement.MAU_ID === template.MA_MAU).length;
    const periodDetails = await payrollRepos.periodDetails.listWhere('MAU_ID', template.MA_MAU);
    if (usedByAgreements || periodDetails.length || template.KHOA_THAY_DOI) {
        await payrollRepos.templates.update([{ MA_MAU: template.MA_MAU, HOAT_DONG: false }]);
        await writeAudit('Ngưng mẫu bảng lương', template.MA_MAU);
        return `Mẫu ${template.MA_MAU} đang được ${usedByAgreements} thỏa thuận và ${periodDetails.length} dòng kỳ lương dùng nên đã chuyển sang ngưng hoạt động.`;
    }
    const columnIds = templateColumns.filter((column) => column.MAU_ID === template.MA_MAU).map((column) => column.ID);
    if (columnIds.length) await payrollRepos.templateColumns.remove(columnIds);
    await payrollRepos.templates.remove([template.MA_MAU]);
    await writeAudit('Xóa mẫu bảng lương', template.MA_MAU);
    return `Đã xóa mẫu ${template.MA_MAU}.`;
}

// ===== THỎA THUẬN LƯƠNG =====

export async function saveAgreement(agreement, { isNew, closeAgreements = [] }) {
    assertCanManagePayroll();
    const rangeError = validateDateRange(agreement.HIEU_LUC_TU, agreement.HIEU_LUC_DEN);
    if (rangeError) throw new Error(rangeError);
    if (!agreement.NHAN_SU_ID) throw new Error('Chọn nhân sự.');
    if (!agreement.MAU_ID) throw new Error('Chọn mẫu bảng lương.');
    // Đóng thỏa thuận cũ trước rồi mới ghi thỏa thuận mới, để không lúc nào có hai thỏa thuận cùng phủ một ngày.
    if (closeAgreements.length) {
        await payrollRepos.agreements.update(closeAgreements.map((item) => ({ ID: item.ID, HIEU_LUC_DEN: item.HIEU_LUC_DEN })));
    }
    const row = {
        ID: agreement.ID || newId('TT'),
        NHAN_SU_ID: agreement.NHAN_SU_ID,
        MAU_ID: agreement.MAU_ID,
        HIEU_LUC_TU: agreement.HIEU_LUC_TU,
        HIEU_LUC_DEN: agreement.HIEU_LUC_DEN || null,
        GIA_TRI: agreement.GIA_TRI || {},
        GHI_CHU: agreement.GHI_CHU || '',
        ...(isNew ? { NGUOI_TAO: currentPayrollUser().username } : {}),
    };
    if (isNew) await payrollRepos.agreements.add([row]);
    else await payrollRepos.agreements.update([row]);
    await writeAudit(isNew ? 'Thêm thỏa thuận lương' : 'Sửa thỏa thuận lương', row);
    return row;
}

export async function addAgreements(rows) {
    assertCanManagePayroll();
    const user = currentPayrollUser().username;
    const prepared = rows.map((row) => ({ ...row, ID: newId('TT'), NGUOI_TAO: user }));
    await payrollRepos.agreements.add(prepared);
    await writeAudit('Nhập thỏa thuận lương từ Excel', `${prepared.length} thỏa thuận`);
    return prepared;
}

export async function deleteAgreement(agreement) {
    assertCanManagePayroll();
    await payrollRepos.agreements.remove([agreement.ID]);
    await writeAudit('Xóa thỏa thuận lương', `${agreement.NHAN_SU_ID} - ${agreement.MAU_ID}`);
}

// ===== PHIẾU ỨNG LƯƠNG =====

export async function loadAdvanceData() {
    const [advances, employees] = await Promise.all([payrollRepos.advances.list(), payrollRepos.employees.list()]);
    return { advances, employees };
}

export async function saveAdvance(advance, { isNew }) {
    assertCanManagePayroll();
    if (!isNew && advance.TRANG_THAI === 'DA_SNAPSHOT') throw new Error('Phiếu đã chốt vào kỳ lương đã duyệt, không sửa được.');
    const errors = validateAdvanceInput(advance);
    if (errors.length) throw new Error(errors.join('\n'));
    const row = {
        MA_PHIEU: advance.MA_PHIEU || newId('UL'),
        NHAN_SU_ID: advance.NHAN_SU_ID,
        NGAY_UNG: advance.NGAY_UNG,
        SO_TIEN: Math.round(Number(advance.SO_TIEN)),
        GHI_CHU: advance.GHI_CHU || '',
        TRANG_THAI: isNew ? 'CHUA_SU_DUNG' : advance.TRANG_THAI,
        ...(isNew ? { NGUOI_TAO: currentPayrollUser().username, BANG_CONG_LUONG_ID: '' } : {}),
    };
    if (isNew) await payrollRepos.advances.add([row]);
    else await payrollRepos.advances.update([row]);
    await writeAudit(isNew ? 'Thêm phiếu ứng lương' : 'Sửa phiếu ứng lương', row);
    return row;
}

export async function deleteAdvance(advance) {
    assertCanManagePayroll();
    if (advance.TRANG_THAI === 'DA_SNAPSHOT') throw new Error('Phiếu đã chốt vào kỳ lương đã duyệt, không xóa được.');
    await payrollRepos.advances.remove([advance.MA_PHIEU]);
    await writeAudit('Xóa phiếu ứng lương', `${advance.MA_PHIEU}: ${advance.NHAN_SU_ID} ${advance.SO_TIEN}`);
}
